import { EMPTY_ARRAY } from 'constants/defaults';
import { HOUR_LAYOUT } from 'constants/hour';
import { DateTime } from 'luxon';
import type { Filter } from 'redux/modules/calendar.types';
import type { Currency } from 'types/account';
import type { HourAsCard } from 'types/hour';
import type { Cost, Duration } from 'types/units';
import { isAfter, isSameDay, isSameMonth, naivelyGetOrdinalSuffix } from 'utils/date-helper';
import { hourCardSizes, summarizeCostAndDuration } from 'utils/hours';
import { fromISO } from 'utils/luxon-helpers';

export interface GroupHoursByDatesSingleTypeUI {
  currency: Currency;
  isTimerRunning: boolean;
  timerCostPerSecond: number;
  timerNumLoggedSecondExact: number;
  timerRunningSince: number;
  totalHourCardHeightPx: number;
  showCostDuration: boolean;
  isToday: boolean;
  isInFuture: boolean;
  isOutsideDay: boolean;
  hourPopoverPosition: 'left-start' | 'right-start';
  /** Total cost for one day including running timer, but floored to minute */
  totalCost: number;
  timerNumLoggedSecondFloored: number;
}

export interface GroupHoursByDatesSingleType {
  key: string;
  hours: Array<HourAsCard>;
  /** Ids of forecasts linked to hours */
  forecastIds: Array<number>;
  title: string;
  day: string;
  cost: Cost;
  duration: Duration; // Exact duration, no rounding on each hour
  durationRoundedDown: Duration; // Total duration where each hour is rounded down to closest minute (where a timer has been used)
  ui: GroupHoursByDatesSingleTypeUI;
}

interface HourTotal {
  billableCost: number;
  loggedTotal: number;
  plannedTotal: number;
  currency?: Currency | false;
}

export interface HourTotals {
  total: HourTotal;
  totalsByClient: Record<number, HourTotal> | null;
  totalsByProject: Record<number, HourTotal> | null;
}

export function calculateHourTotals(
  hours: Array<HourAsCard>,
  filter: Pick<Filter, 'span' | 'date'>
) {
  return hours.reduce<HourTotals>(
    (
      { total, totalsByClient, totalsByProject },
      {
        clientId,
        projectId,
        numLoggedMoney,
        numPlannedSeconds,
        numLoggedSecondsRoundedDown,
        isBillable,
        day,
        projectCurrency,
      }
    ) => {
      const clientTotals = totalsByClient?.[clientId] ?? {
        billableCost: 0,
        loggedTotal: 0,
        plannedTotal: 0,
      };

      const projectTotals = totalsByProject?.[projectId] ?? {
        billableCost: 0,
        loggedTotal: 0,
        plannedTotal: 0,
      };

      const isOutsideDay = filter && filter.span === 'month' && !isSameMonth(day, filter.date);

      if (isOutsideDay) {
        return { total, totalsByClient, totalsByProject };
      }

      return {
        total: {
          loggedTotal: (total.loggedTotal += numLoggedSecondsRoundedDown),
          plannedTotal: (total.plannedTotal += numPlannedSeconds),
          billableCost: isBillable ? (total.billableCost += numLoggedMoney) : total.billableCost,
          currency:
            typeof total.currency === 'undefined' ||
            (!!total.currency && projectCurrency.id === total.currency.id)
              ? projectCurrency
              : false,
        },
        totalsByClient: {
          ...totalsByClient,
          [clientId]: {
            loggedTotal: (clientTotals.loggedTotal += numLoggedSecondsRoundedDown),
            plannedTotal: (clientTotals.plannedTotal += numPlannedSeconds),
            billableCost: isBillable
              ? (clientTotals.billableCost += numLoggedMoney)
              : clientTotals.billableCost,
            currency:
              typeof clientTotals.currency === 'undefined' ||
              (!!clientTotals.currency && projectCurrency.id === clientTotals.currency.id)
                ? projectCurrency
                : false,
          },
        },
        totalsByProject: {
          ...totalsByProject,
          [projectId]: {
            loggedTotal: (projectTotals.loggedTotal += numLoggedSecondsRoundedDown),
            plannedTotal: (projectTotals.plannedTotal += numPlannedSeconds),
            billableCost: isBillable
              ? (projectTotals.billableCost += numLoggedMoney)
              : projectTotals.billableCost,
            currency:
              typeof projectTotals.currency === 'undefined' ||
              (!!projectTotals.currency && projectCurrency.id === projectTotals.currency.id)
                ? projectCurrency
                : false,
          },
        },
      };
    },
    {
      total: { billableCost: 0, loggedTotal: 0, plannedTotal: 0 },
      totalsByClient: {},
      totalsByProject: {},
    }
  );
}

export interface GroupHoursByDatesType {
  [date: string]: GroupHoursByDatesSingleType;
}

interface GroupHoursByDatesParams {
  hours: Array<HourAsCard>;
  filter: Filter;
  dates: Array<string>;
  sizeBasedOnDuration?: boolean;
  currency: Currency;
}
export function groupHoursByDates({
  hours,
  filter,
  dates,
  sizeBasedOnDuration = false,
  currency,
}: GroupHoursByDatesParams): GroupHoursByDatesType {
  return dates.reduce((prev, day, index) => {
    const linkedForecastIdsByDay: Record<string, Array<number>> = {};
    const hoursInDay =
      hours.filter(hour => {
        if (hour.forecastId && hour.day === day) {
          (linkedForecastIdsByDay[day] ??= []).push(hour.forecastId);
        }
        return hour.day === day;
      }) || EMPTY_ARRAY;

    const hourWithRunningTimer = hoursInDay.find(hour => hour.isTimerRunning);

    const isTimerRunning = hourWithRunningTimer !== undefined;

    const totalHourCardHeightPx = hoursInDay.reduce((prevPx, hour) => {
      // 2nd param is sizeBasedOnDuration
      // todo: Correctly set this via filter/something (currently lives in getMultiUserCalendarProps)
      const sizes = hourCardSizes(hour, sizeBasedOnDuration);

      const isJustPlanned = !hour.numLoggedSecondsExact && !!hour.numPlannedSeconds;
      const height = Math.max(
        isJustPlanned ? sizes.plannedHeightPx : sizes.loggedHeightPx,
        HOUR_LAYOUT.minHeight
      );

      return prevPx + height;
    }, 0);

    // Values of the running timer is ignored, since we need to keep the
    // cost & duration updated in real time.
    const { cost, duration, durationRoundedDown } = summarizeCostAndDuration(hoursInDay, {
      ignoreRunningTimer: true,
    });

    const loggedSecondsExact =
      (hourWithRunningTimer && hourWithRunningTimer.numLoggedSecondsExact) || 0;
    const loggedSecondsFlooredToMinute = Math.floor(loggedSecondsExact / 60) * 60;
    let timerCost = 0;
    if (hourWithRunningTimer) {
      timerCost = loggedSecondsFlooredToMinute * hourWithRunningTimer.costPerSecond;
    }

    // Total cost for one day including running timer, but floored to minute
    const totalCostIncludingTimerFloored = ((cost && cost.amount) || 0) + timerCost;

    const today = DateTime.now().toISODate();
    const dtDay = fromISO(day);
    const dayName = dtDay.weekdayLong;
    const dayShortName = dtDay.weekdayShort;
    const dayNumber = naivelyGetOrdinalSuffix(dtDay.day);
    const isToday = isSameDay(day, today);
    const isInFuture = isAfter(day, today);
    const isWeekOrLess = dates.length <= 7;
    const isSingleDay = dates.length === 1;
    // If day is outside what we're actually looking for
    // (ie, monday of last month, if "today" is Tuesday the 1st)
    const isOutsideDay = filter.span === 'month' && !isSameMonth(day, filter.date);

    // Determine origin of HourWithHourEditorModal
    const numColumns = dates.length <= 7 ? dates.length : 7;
    const hourPopoverPosition = index % numColumns < 3 ? 'right-start' : 'left-start';

    const title =
      isToday && isSingleDay
        ? `Today, ${dayNumber}`
        : isToday
          ? 'Today'
          : isSingleDay
            ? `${dayName}, ${dayNumber}`
            : isWeekOrLess
              ? `${dayShortName}, ${dayNumber}`
              : dayName;

    const perDayGroup: GroupHoursByDatesSingleType = {
      key: day,
      hours: hoursInDay,
      forecastIds: linkedForecastIdsByDay[day],
      title,
      day: dayNumber,
      // @todo move some of the stuff above into this key
      ui: {
        currency,
        totalHourCardHeightPx,
        hourPopoverPosition,
        showCostDuration: isWeekOrLess,
        isToday,
        isInFuture,
        isOutsideDay,
        isTimerRunning,
        timerCostPerSecond: hourWithRunningTimer?.costPerSecond || 0,
        timerNumLoggedSecondExact: hourWithRunningTimer?.numLoggedSecondsExact || 0,
        timerRunningSince: hourWithRunningTimer?.timerRunningSince || 0,
        totalCost: totalCostIncludingTimerFloored,
        timerNumLoggedSecondFloored: loggedSecondsFlooredToMinute,
      },
      cost,
      duration,
      durationRoundedDown,
    };

    return {
      ...prev,
      [day]: perDayGroup,
    };
  }, {});
}
